// src/pages/paidProject/ReportPage.jsx

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import {
  ChartBarIcon,
  DocumentTextIcon,
  CalculatorIcon,
  CurrencyRupeeIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from "@heroicons/react/outline";

import PlantMachinery from "./Annexure 1/PlantMachinery";
import FurnitureAndFixture from "./Annexure 1/FurnitureAndFixture";
import TermLoanRepayment from "./Annexure 1/TermLoanRepayment";
import SensitivityAnalysis from "./Annexure 1/SensitivityAnalysis";
import ProjectedCashFlow from "./Annexure 1/ProjectedCashFlow";
import ProjectedBalanceSheet from "./Annexure 1/ProjectedBalanceSheet";
import BreakEvenPointAnalysis from "./Annexure 1/BreakEvenPointAnalysis";
import ProjectedProfitability from "./Annexure 1/ProjectedProfitability";
import WorkingCapitalPage from "./Annexure 1/WorkingCapitalPage";
import AnnualSalesRealisation from "./Annexure 1/AnnualSalesRealisation";
import Power from "./Annexure 1/Power";
import CostOfProduction from "./Annexure 1/CostOfProduction";
import RMConsumption from "./Annexure 1/RMConsumption";
import CostOfTheProject from "./Annexure 1/CostOfTheProject";
import Depreciation from "./Annexure 1/Depreciation";
import CalculateLabour from "./Annexure 1/CalculateLabour";
import Consumables from "./Annexure 1/Consumables";
import PreliminaryExpenses from "./Annexure 1/PreliminaryExpenses";
import ComputerSoftware from "./Annexure 1/ComputerSoftware";
import OtherFixedAssets from "./Annexure 1/OtherFixedAssets";
import API from "../../api";

const SECTION_CONFIG = {
  costOfProject: {
    Icon: CurrencyRupeeIcon,
    title: "Cost of the Project (Rs in Lakhs)",
    ContentComponent: CostOfTheProject,
  },
  plantMachinery: {
    Icon: DocumentTextIcon,
    title: "Plant & Machinery (Rs in Lakhs)",
    ContentComponent: PlantMachinery,
  },
  furniture: {
    Icon: DocumentTextIcon,
    title: "Furniture & Fixtures (Rs in Lakhs)",
    ContentComponent: FurnitureAndFixture,
  },
  otherFixedAssets: {
    Icon: DocumentTextIcon,
    title: "Other Fixed Assets (Rs in Lakhs)",
    ContentComponent: OtherFixedAssets,
  },
  computers: {
    Icon: DocumentTextIcon,
    title: "Computers & Software (Rs in Lakhs)",
    ContentComponent: ComputerSoftware,
  },
  preliminaryExpenses: {
    Icon: DocumentTextIcon,
    title: "Preliminary Expenses (Rs in Lakhs)",
    ContentComponent: PreliminaryExpenses,
  },
  consumablesExpenseDetails: {
    Icon: CalculatorIcon,
    title: "Consumables Expense Details (Rs in Lakhs)",
    ContentComponent: Consumables,
  },
  labourCost: {
    Icon: DocumentTextIcon,
    title: "Labour Cost Calculation (Rs in Lakhs)",
    ContentComponent: CalculateLabour,
  },
  depreciation: {
    Icon: DocumentTextIcon,
    title: "Depreciation (Rs in Lakhs)",
    ContentComponent: Depreciation,
  },
  rmConsumption: {
    Icon: DocumentTextIcon,
    title: "RM Consumption (Rs in Lakhs)",
    ContentComponent: RMConsumption,
  },
  costOfProduction: {
    Icon: DocumentTextIcon,
    title: "Cost of Production (Rs in Lakhs)",
    ContentComponent: CostOfProduction,
  },
  powerSection: {
    Icon: DocumentTextIcon,
    title: "Power (Rs in Lakhs)",
    ContentComponent: Power,
  },
  annualSales: {
    Icon: DocumentTextIcon,
    title: "Annual Sales Realisation (Rs in Lakks)",
    ContentComponent: AnnualSalesRealisation,
  },
  workingCapitalRequirement: {
    Icon: DocumentTextIcon,
    title: "Working Capital Requirement (Rs in Lakhs)",
    ContentComponent: WorkingCapitalPage,
  },
  projectedProfitability: {
    Icon: CalculatorIcon,
    title: "Projected Profitability (Rs in Lakhs)",
    ContentComponent: ProjectedProfitability,
  },
  breakEven: {
    Icon: CalculatorIcon,
    title: "Break-Even Point Analysis (Rs in Lakhs)",
    ContentComponent: BreakEvenPointAnalysis,
  },
  balanceSheet: {
    Icon: DocumentTextIcon,
    title: "Balance Sheet (Rs in Lakhs)",
    ContentComponent: ProjectedBalanceSheet,
  },
  projectedCashFlow: {
    Icon: CurrencyRupeeIcon,
    title: "Projected Cash Flow & Promoter’s Drawings (Rs in Lakhs)",
    ContentComponent: ProjectedCashFlow,
  },
  sensitivityAnalysis: {
    Icon: ChartBarIcon,
    title: "Sensitivity Analysis (Rs in Lakhs)",
    ContentComponent: SensitivityAnalysis,
  },
  termLoanRepayment: {
    Icon: DocumentTextIcon,
    title: "EMI Schedule (Rs in Lakhs)",
    ContentComponent: TermLoanRepayment,
  },
};

export default function ReportPage() {
  const { project } = useOutletContext();
  const sectionKeys = Object.keys(SECTION_CONFIG);

  const defaultClosed = useMemo(
    () =>
      sectionKeys.reduce((acc, k) => {
        acc[k] = false;
        return acc;
      }, {}),
    [sectionKeys]
  );

  const [openSections, setOpenSections] = useState(defaultClosed);

  // ✅ Balance sheet mismatch state (read from localStorage + event)
  const [bsMismatch, setBsMismatch] = useState(false);
  const [bsMismatchDetails, setBsMismatchDetails] = useState([]);

  const projectId = project?._id;

  const readMismatchFromStorage = useCallback(() => {
    if (!projectId) return { mismatch: false, diffs: [] };
    try {
      const raw = localStorage.getItem(`balanceSheetMismatch:${projectId}`);
      if (!raw) return { mismatch: false, diffs: [] };
      const parsed = JSON.parse(raw);
      return {
        mismatch: !!parsed?.mismatch,
        diffs: Array.isArray(parsed?.diffs) ? parsed.diffs : [],
      };
    } catch {
      return { mismatch: false, diffs: [] };
    }
  }, [projectId]);

  // Initial load + listen to mismatch event from ProjectedBalanceSheet component
  useEffect(() => {
    const initial = readMismatchFromStorage();
    setBsMismatch(initial.mismatch);
    setBsMismatchDetails(initial.diffs);

    const onEvent = (e) => {
      if (!e?.detail) return;
      if (projectId && e.detail.projectId && e.detail.projectId !== projectId)
        return;
      setBsMismatch(!!e.detail.mismatch);
      setBsMismatchDetails(Array.isArray(e.detail.diffs) ? e.detail.diffs : []);
    };

    window.addEventListener("balanceSheet:mismatch", onEvent);
    return () => window.removeEventListener("balanceSheet:mismatch", onEvent);
  }, [readMismatchFromStorage, projectId]);

  // ✅ Disable printing (Ctrl+P / Cmd+P and window.print) for:
  // 1) UNPAID projects
  // 2) PAID but Balance Sheet mismatch
  useEffect(() => {
    if (!project) return;

    const shouldBlockPrint = !project.isPaid || bsMismatch;
    if (!shouldBlockPrint) return;

    const originalPrint = window.print;

    const msg = !project.isPaid
      ? "Printing is disabled until you complete payment for this project."
      : "Printing is disabled because Balance Sheet data is not matching. Please fix it first.";

    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "p") {
        e.preventDefault();
        alert(msg);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    window.print = () => {
      alert(msg);
    };

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.print = originalPrint;
    };
  }, [project, bsMismatch]);

  // Expand all, print, then restore (used only when paid AND match)
  const handlePrint = () => {
    if (!project?.isPaid) return;
    if (bsMismatch) {
      alert(
        "Printing is disabled because Balance Sheet data is not matching. Please fix it first."
      );
      return;
    }

    const allOpen = sectionKeys.reduce((acc, k) => ((acc[k] = true), acc), {});
    setOpenSections(allOpen);

    setTimeout(() => {
      window.print();
      setOpenSections(defaultClosed);
    }, 300);
  };
  useEffect(() => {
    if (!bsMismatch) return;

    setOpenSections((prev) => ({
      ...prev,
      balanceSheet: true, // 🔥 open this section automatically
    }));

    // optional: auto scroll to that section
    setTimeout(() => {
      document.getElementById("balanceSheet")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 150);
  }, [bsMismatch]);

  const toggleSection = (key) =>
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));

  // Razorpay helpers for paying directly from "Cost of the Project" section
  const loadRzp = () =>
    new Promise((resolve) => {
      if (window.Razorpay) return resolve(true);
      const s = document.createElement("script");
      s.src = "https://checkout.razorpay.com/v1/checkout.js";
      s.onload = () => resolve(true);
      s.onerror = () => resolve(false);
      document.body.appendChild(s);
    });

  const handlePayFromReport = async () => {
    if (!project) return;

    const ok = await loadRzp();
    if (!ok) {
      alert("Could not load payment gateway");
      return;
    }

    try {
      const { data: order } = await API.post(
        `/user/projects/${project._id}/order`
      );

      new window.Razorpay({
        key: order.key,
        amount: order.amount,
        currency: order.currency,
        order_id: order.id,
        name: "YourAppName",
        description: "Project Plan Payment",
        prefill: { email: project.email, contact: project.phone },
        handler: async () => {
          try {
            await API.post(`/user/projects/${project._id}/pay`);
            alert("Payment successful! Reloading your project report...");
            window.location.reload();
          } catch (e) {
            console.error(e);
            alert("Payment verification failed");
          }
        },
        theme: { color: "#1976d2" },
      }).open();
    } catch (err) {
      console.error(err);
      alert("Failed to initiate payment");
    }
  };

  return (
    <div className="p-6 bg-gray-50 space-y-4 print-container">
      {/* ✅ PRINT FIX: reserve bottom space + fixed footer so it never overlaps tables */}
      <style>{`
        @media print {
          @page { margin: 12mm 10mm 26mm 10mm; }

          .print-container {
            padding-bottom: 28mm !important;
            background: #fff !important;
          }

          .print-footer {
            position: fixed;
            left: 0;
            right: 0;
            bottom: 6mm;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            font-size: 13px;
            letter-spacing: 0.06em;
            background: #fff;
            padding: 2mm 0;
            z-index: 9999;
          }

          .accordion-wrap { max-height: none !important; overflow: visible !important; }
          .accordion-fade { opacity: 1 !important; }
          .printable-section { box-shadow: none !important; }
        }
      `}</style>

      {/* ✅ Global mismatch banner (screen only) */}
      {project?.isPaid && bsMismatch && (
        <div className="no-print rounded-md border border-red-300 bg-red-50 p-3 text-red-800">
          <div className="font-semibold">Balance Sheet mismatch detected</div>
          <div className="text-sm">
            Printing is disabled until Balance Sheet totals match.
          </div>
        </div>
      )}

      {/* Print PDF – only if project is paid AND no mismatch */}
      {project?.isPaid && (
        <div className="flex justify-end space-x-2 no-print">
          <button
            onClick={handlePrint}
            disabled={bsMismatch}
            className={`px-3 py-1 rounded text-sm text-white ${
              bsMismatch
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-green-600 hover:bg-green-700"
            }`}
            title={
              bsMismatch
                ? "Printing disabled: Balance Sheet mismatch"
                : "Print PDF"
            }
          >
            Print PDF
          </button>
        </div>
      )}

      {/* Watermark */}
      {project && <div className="print-watermark">{project.companyName}</div>}

      {sectionKeys.map((key) => {
        // If NOT paid → only show "Cost of the Project" section
        if (!project?.isPaid && key !== "costOfProject") return null;

        const { Icon, title, ContentComponent } = SECTION_CONFIG[key];
        const isOpen = openSections[key];

        return (
          <section
            key={key}
            id={key}
            className="bg-white rounded shadow printable-section"
            style={{ pageBreakAfter: "always" }}
          >
            {/* Printed header on each page */}
            {project && (
              <div className="hidden print:block text-center mb-2">
                <p className="font-bold text-xl">{project.companyName}</p>
                <p>{project.companyAddress}</p>
                <p>{project.idNumber}</p>
                <p>{project.projectType}</p>
                <p>{project.productNames}</p>
              </div>
            )}

            {/* Section header (screen view only) */}
            <div className="flex items-center justify-between bg-gray-100 p-3 rounded-t no-print">
              <div className="flex items-center space-x-2">
                <Icon className="h-5 w-5 text-gray-700" />
                <h2 className="text-base font-semibold text-gray-700">
                  {title}
                </h2>

                {/* ✅ reflect mismatch badge on Balance Sheet section */}
                {key === "balanceSheet" && project?.isPaid && bsMismatch && (
                  <span className="ml-2 inline-flex items-center rounded-full bg-red-600 px-2 py-0.5 text-xs font-semibold text-white">
                    Mismatch
                  </span>
                )}
              </div>

              <button
                onClick={() => toggleSection(key)}
                className="p-1 bg-gray-200 hover:bg-gray-300 rounded-full"
              >
                {isOpen ? (
                  <ChevronUpIcon className="h-4 w-4 text-gray-700" />
                ) : (
                  <ChevronDownIcon className="h-4 w-4 text-gray-700" />
                )}
              </button>
            </div>

            {/* Printed section title (for print only) */}
            <div className="hidden print:flex items-center justify-center bg-gray-100 p-2">
              <h2 className="text-base font-semibold text-gray-700">{title}</h2>
            </div>

            {/* Expand/collapse area */}
            <div
              className={`accordion-wrap overflow-hidden transition-max-height duration-300 ease-in-out ${
                isOpen ? "max-h-full" : "max-h-0"
              }`}
            >
              <div
                className={`accordion-fade transition-opacity duration-200 ease-in-out ${
                  isOpen ? "opacity-100" : "opacity-0"
                } bg-white p-3 rounded-b`}
              >
                {/* ✅ show mismatch warning inside balance sheet section */}
                {key === "balanceSheet" && project?.isPaid && bsMismatch && (
                  <div className="mb-3 rounded-md border border-red-300 bg-red-50 p-3 text-red-800">
                    <div className="font-semibold">
                      Balance Sheet is not matching
                    </div>
                    <div className="text-sm">
                      Liabilities Total must equal TOTAL (A+B+C). Printing is
                      disabled until fixed.
                    </div>
                  </div>
                )}

                {ContentComponent ? (
                  key === "costOfProject" ? (
                    <>
                      <ContentComponent />
                      {!project?.isPaid && (
                        <div className="mt-4 flex justify-end no-print">
                          <button
                            onClick={handlePayFromReport}
                            className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                          >
                            Pay for Project
                          </button>
                        </div>
                      )}
                    </>
                  ) : (
                    <ContentComponent />
                  )
                ) : (
                  <div className="h-16 border-2 border-dashed border-gray-300 rounded flex items-center justify-center text-gray-400 text-sm">
                    Content goes here
                  </div>
                )}
              </div>
            </div>
          </section>
        );
      })}

      {/* ✅ Global PRINT FOOTER (fixed, repeated on every printed page, no overlap) */}
      <div className="hidden print:flex print-footer">
        <span>THIS REPORT IS GENERATED IN</span>
        <img
          src="/logo.png"
          alt="Logo"
          className="h-8 w-20 inline-block align-middle"
        />
        <span>SOFT</span>
      </div>
    </div>
  );
}
